import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cvAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  FileText, Upload, Sparkles, Loader2, LogOut, ChevronRight, ArrowLeft,
  User, Globe, FileType, Palette, Image, Camera
} from 'lucide-react';

const TEMPLATES = [
  { id: 'template-a', name: 'Navy Sidebar', description: 'Professional two-column with navy sidebar', color: 'bg-blue-900', hasPhoto: true },
  { id: 'template-a-no-photo', name: 'Navy Sidebar (No Photo)', description: 'Two-column without photo', color: 'bg-blue-900', hasPhoto: false },
  { id: 'template-b', name: 'ATS Standard', description: 'Clean single-column, ATS-optimized', color: 'bg-zinc-100', hasPhoto: true },
  { id: 'template-b-no-photo', name: 'ATS Standard (No Photo)', description: 'ATS-friendly without photo', color: 'bg-zinc-100', hasPhoto: false },
  { id: 'template-c', name: 'Modern Minimal', description: 'Contemporary with blue accent', color: 'bg-blue-500', hasPhoto: true },
  { id: 'template-c-no-photo', name: 'Modern Minimal (No Photo)', description: 'Modern style without photo', color: 'bg-blue-500', hasPhoto: false },
];

const Generate = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);

  const [formData, setFormData] = useState({
    candidateName: '',
    photoUrl: '',
    rawText: '',
    instructions: '',
    language: 'en',
    serviceType: 'cv_700',
    templateId: 'template-a',
  });

  const [uploadedFile, setUploadedFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [error, setError] = useState('');

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadedFile(file);
    setExtracting(true);
    setError('');

    try {
      const result = await cvAPI.extractText(file);
      setFormData(prev => ({ ...prev, rawText: result.text }));
    } catch (err) {
      setError('Failed to extract text from file. Please try again or paste the content manually.');
    } finally {
      setExtracting(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingPhoto(true);
    setError('');

    try {
      const result = await cvAPI.uploadPhoto(file);
      setFormData(prev => ({ ...prev, photoUrl: result.photoUrl }));
    } catch (err) {
      setError('Failed to upload photo. Please try again.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleGenerate = async () => {
    if (!formData.candidateName.trim()) {
      setError('Please enter the candidate name');
      return;
    }
    if (!formData.rawText.trim()) {
      setError('Please provide CV content');
      return;
    }

    setGenerating(true);
    setError('');

    try {
      const result = await cvAPI.generate({
        candidateName: formData.candidateName,
        rawText: formData.rawText,
        instructions: formData.instructions,
        language: formData.language,
        templateId: formData.templateId,
        photoUrl: formData.photoUrl,
      });
      navigate(`/edit/${result.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate CV. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50/50 flex flex-col" data-testid="generate-page">
      {/* Header */}
      <header className="h-14 md:h-16 border-b border-zinc-200 bg-white flex items-center justify-between px-4 md:px-6 shrink-0">
        <div className="flex items-center gap-2 md:gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard"
            className="px-2 md:px-3"
          >
            <ArrowLeft className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Back</span>
          </Button>
          <div className="w-8 h-8 md:w-10 md:h-10 bg-zinc-900 rounded-lg flex items-center justify-center">
            <FileText className="w-4 h-4 md:w-5 md:h-5 text-white" />
          </div>
          <span className="font-outfit font-semibold text-base md:text-lg hidden sm:inline">MoCV Studio</span>
        </div>
        <div className="flex items-center gap-2 md:gap-4">
          <span className="text-xs md:text-sm text-zinc-500 hidden sm:inline">{user?.name}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            data-testid="logout-btn"
            className="px-2 md:px-3"
          >
            <LogOut className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Sign Out</span>
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center py-6 md:py-12 px-4">
        <div className="w-full max-w-3xl">
          {/* Page Title */}
          <div className="text-center mb-6 md:mb-10">
            <h1 className="font-outfit text-2xl md:text-4xl font-semibold tracking-tight text-zinc-900 mb-2">
              Create Your CV
            </h1>
            <p className="text-zinc-500 font-dm-sans text-sm md:text-base">
              Upload your existing CV or paste content to get started
            </p>
          </div>

          {/* Form Card */}
          <Card className="border border-zinc-200 shadow-sm">
            <CardContent className="p-4 md:p-8 lg:p-12 space-y-6 md:space-y-8">
              {/* Candidate Information Section */}
              <section className="space-y-4 md:space-y-6">
                <div className="flex items-center gap-2 text-zinc-900">
                  <User className="w-5 h-5" />
                  <h2 className="font-outfit font-medium text-base md:text-lg">Candidate Information</h2>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 md:gap-6">
                  {/* Photo Upload */}
                  <div className="flex flex-row sm:flex-col items-center gap-4 sm:gap-2">
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg"
                      onChange={handlePhotoUpload}
                      className="hidden"
                      data-testid="photo-file-input"
                    />
                    <button
                      onClick={() => photoInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-zinc-100 border-2 border-dashed border-zinc-300 flex items-center justify-center overflow-hidden hover:border-zinc-400 transition-colors relative flex-shrink-0"
                      data-testid="photo-upload-btn"
                    >
                      {uploadingPhoto ? (
                        <Loader2 className="w-6 h-6 text-zinc-400 animate-spin" />
                      ) : formData.photoUrl ? (
                        <>
                          <img 
                            src={formData.photoUrl} 
                            alt="Preview" 
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                            <Camera className="w-6 h-6 text-white" />
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-col items-center">
                          <Image className="w-6 h-6 text-zinc-400" />
                          <span className="text-[10px] text-zinc-400 mt-1">Upload</span>
                        </div>
                      )}
                    </button>
                    <span className="text-xs text-zinc-400">Photo (optional)</span>
                  </div>
                  
                  {/* Name and Photo URL */}
                  <div className="flex-1 space-y-3 md:space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="candidateName">Candidate Name *</Label>
                      <Input
                        id="candidateName"
                        placeholder="John Doe"
                        value={formData.candidateName}
                        onChange={(e) => setFormData(prev => ({ ...prev, candidateName: e.target.value }))}
                        data-testid="candidate-name-input"
                        className="h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="photoUrl">Or paste Photo URL</Label>
                      <Input
                        id="photoUrl"
                        placeholder="https://example.com/photo.jpg"
                        value={formData.photoUrl}
                        onChange={(e) => setFormData(prev => ({ ...prev, photoUrl: e.target.value }))}
                        data-testid="photo-url-input"
                        className="h-12"
                      />
                    </div>
                  </div>
                </div>

                {/* File Upload */}
                <div className="space-y-2">
                  <Label>Upload Existing CV (PDF/DOCX/TXT)</Label>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-zinc-200 rounded-lg p-8 text-center cursor-pointer hover:border-zinc-400 hover:bg-zinc-50 transition-colors"
                    data-testid="file-upload-area"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.docx,.doc,.txt"
                      onChange={handleFileUpload}
                      className="hidden"
                      data-testid="file-input"
                    />
                    {extracting ? (
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className="w-8 h-8 text-zinc-400 animate-spin" />
                        <p className="text-sm text-zinc-500">Extracting text...</p>
                      </div>
                    ) : uploadedFile ? (
                      <div className="flex flex-col items-center gap-2">
                        <FileType className="w-8 h-8 text-green-600" />
                        <p className="text-sm text-zinc-700">{uploadedFile.name}</p>
                        <p className="text-xs text-zinc-400">Click to upload a different file</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <Upload className="w-8 h-8 text-zinc-400" />
                        <p className="text-sm text-zinc-500">
                          Drag and drop or <span className="text-zinc-900 font-medium">browse</span>
                        </p>
                        <p className="text-xs text-zinc-400">PDF, DOCX, or TXT up to 10MB</p>
                      </div>
                    )}
                  </div>
                </div>
              </section>

              {/* CV Settings Section */}
              <section className="space-y-4 md:space-y-6">
                <div className="flex items-center gap-2 text-zinc-900">
                  <Globe className="w-5 h-5" />
                  <h2 className="font-outfit font-medium text-base md:text-lg">CV Settings</h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                  <div className="space-y-2">
                    <Label>Language</Label>
                    <Select
                      value={formData.language}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, language: value }))}
                    >
                      <SelectTrigger className="h-12" data-testid="language-select">
                        <SelectValue placeholder="Select language" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="fr">French</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Service Type</Label>
                    <Select
                      value={formData.serviceType}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, serviceType: value }))}
                    >
                      <SelectTrigger className="h-12" data-testid="service-type-select">
                        <SelectValue placeholder="Select service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cv_700">CV (Rs 700)</SelectItem>
                        <SelectItem value="cv_premium">CV Premium (Rs 1200)</SelectItem>
                        <SelectItem value="cv_executive">CV Executive (Rs 2000)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </section>

              {/* Template Selection */}
              <section className="space-y-4 md:space-y-6">
                <div className="flex items-center gap-2 text-zinc-900">
                  <Palette className="w-5 h-5" />
                  <h2 className="font-outfit font-medium text-base md:text-lg">Select Template</h2>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 md:gap-3">
                  {TEMPLATES.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => setFormData(prev => ({ ...prev, templateId: template.id }))}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        formData.templateId === template.id
                          ? 'border-zinc-900 bg-zinc-50'
                          : 'border-zinc-200 hover:border-zinc-300'
                      }`}
                      data-testid={`template-${template.id}`}
                    >
                      <div className={`w-full h-12 rounded ${template.color} mb-2`} />
                      <p className="text-xs font-medium text-zinc-900 truncate">{template.name}</p>
                      <p className="text-[10px] text-zinc-500 truncate">{template.description}</p>
                    </button>
                  ))}
                </div>
              </section>

              {/* CV Content Section */}
              <section className="space-y-4 md:space-y-6">
                <div className="flex items-center gap-2 text-zinc-900">
                  <FileText className="w-5 h-5" />
                  <h2 className="font-outfit font-medium text-base md:text-lg">CV Content</h2>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rawText">Raw CV Text *</Label>
                  <Textarea
                    id="rawText"
                    placeholder="Paste your CV content here or upload a file above..."
                    value={formData.rawText}
                    onChange={(e) => setFormData(prev => ({ ...prev, rawText: e.target.value }))}
                    rows={10}
                    data-testid="raw-text-input"
                    className="font-mono text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="instructions">Requested Changes / Instructions (optional)</Label>
                  <Textarea
                    id="instructions"
                    placeholder="e.g., Make the summary more concise, emphasize leadership skills..."
                    value={formData.instructions}
                    onChange={(e) => setFormData(prev => ({ ...prev, instructions: e.target.value }))}
                    rows={3}
                    data-testid="instructions-input"
                  />
                </div>
              </section>

              {/* Error Message */}
              {error && (
                <div className="text-sm text-red-600 bg-red-50 p-4 rounded-lg" data-testid="generate-error">
                  {error}
                </div>
              )}

              {/* Generate Button */}
              <Button
                onClick={handleGenerate}
                disabled={generating}
                className="w-full h-12 md:h-14 bg-zinc-900 hover:bg-zinc-800 text-sm md:text-base font-medium"
                data-testid="generate-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Generating CV with AI...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-5 w-5" />
                    Generate & Enhance CV
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Generate;
